/**
 * CURRENCY KEEP — THE RAW-NOTE NARROWER (Phase 6.5 step 6, stop 3).
 *
 * A RAW note has no label and no Fr number printed on it. A reading (the
 * v3 raw prompt, `encoder/prompt-raw-note.mjs`) gives what IS printed —
 * denomination, series, type, the serial star, a state or colony, an FRB
 * district — and this module turns that into CANDIDATES from the
 * catalogue. ⚠⚠ IT NEVER PICKS ONE. His plan: *"NARROW the catalog, do not
 * pick from it … a confident wrong Fr# is worse than no autofill."* The
 * collector chooses, with signature images beside each candidate
 * (decision 5, not built).
 *
 * ── THE RULE EVERY AXIS FOLLOWS: A ROW IS DROPPED ONLY BY EVIDENCE ────
 * An axis filters only when the READING carries it, and a row the
 * catalogue cannot answer for on that axis is KEPT, never dropped —
 * excluding it would be a claim about the catalogue's prose, not about
 * the note (the 09-18 `raw-narrow` rule). A truth EXCLUDED from the set is
 * the catastrophic failure; a set that is merely large is honest.
 *
 * ── ⚠⚠ THE TYPE MAP IS GENEROUS, AND IT NARROWS ONLY — HIS RULING
 * 2026-09-25 ─────────────────────────────────────────────────────────
 * The note PRINTS a heading; the catalogue stores a collector's SECTION.
 * His 09-18 ruling 4 refused a builder's heading→section table for
 * SCORING type, and that stands. For NARROWING he ruled *"Generous map,
 * narrow only"*: a heading maps to EVERY section that can print it, never
 * fewer, and a heading the map does not know narrows nothing. `Error Note`
 * rides on every heading, because an error note prints an ordinary one.
 *
 * Pure: in a reading and the catalogue's rows, out a result object. No
 * state, no storage, no DOM.
 */

import { seriesText } from './note-filters.js'
import { normDenomination } from './slab-match.js'

export const BAR = 12 // his usability bar: "twelve candidates with signatures is still a usable screen"

/** ⚠ HIS RULING (8), 2026-09-29: the four Fractional rows the book says do
 *  not exist (*"The note previously listed does not exist."*) — *"Hide
 *  them, don't remove them … The card never offers them and catalogue
 *  search never shows them; a saved note that points to one still shows
 *  as it does today."* So they stay in the catalogue and in `BY_SID`; the
 *  narrower's index and the Resources list skip them, by sid, here in ONE
 *  place. `catalog-amend-check` asserts this list equals the plan's. */
export const HIDDEN_SIDS = Object.freeze(['FRAC:1260', 'FRAC:1270', 'FRAC:1293', 'FRAC:1375'])
export const isHidden = (row) => !!row && HIDDEN_SIDS.includes(row.sid)

export const CONTINENTAL_ISSUER = 'The United Colonies'
export const FEDERAL_ISSUER = 'The United States'

export const FRB_DISTRICTS = Object.freeze(['Boston', 'New York', 'Philadelphia', 'Cleveland', 'Richmond', 'Atlanta', 'Chicago', 'St. Louis', 'Minneapolis', 'Kansas City', 'Dallas', 'San Francisco'])

/** Printed heading (lower case, matched as a substring) → every catalogue
 *  section a note printing it can be in. Generous by his ruling; shown to
 *  him before any keyed run uses it.
 *  ⚠ TWO CHANGES, HIS RULINGS 2026-09-27 (step 6 review): WWII leaves
 *  *united states note* ONLY — *"Hawaii $1 and all North Africa notes are
 *  Silver Certificates; Hawaii $5, $10 and $20 are Federal Reserve Notes
 *  (San Francisco). Keep WWII under both types."* / *"drop it only from
 *  United States Note"*; and *state bank* / *obsolete* reach Obsolete
 *  Note — *"A reading like "State Bank Note" counts as Obsolete"*. */
export const HEADING_SECTIONS = Object.freeze({
  'united states note': ['Legal Tender', 'Small Size Legal Tender'],
  'legal tender': ['Legal Tender', 'Small Size Legal Tender'],
  'silver certificate': ['Silver Certificate', 'Small Size Silver Certificate', 'WWII Emergency Note'],
  'federal reserve note': ['Federal Reserve', 'Small Size Federal Reserve Note', 'WWII Emergency Note'],
  'federal reserve bank note': ['Large Size Federal Reserve Bank Note', 'Small Size Federal Reserve Bank Note'],
  /* ⚠ HIS #3, 2026-09-28: *"National Gold Banknotes are not Large Size
   * National Bank Notes and should be their own type."* So the two National
   * headings no longer reach them — ~~'Large Size National Gold Bank Note'~~
   * was the last entry of both lists until 2026-10-01; 'national gold bank'
   * below is the heading that reaches them. */
  'national currency': ['Large Size National Bank Note', 'Small Size National Currency', 'Small Size Federal Reserve Bank Note'],
  'national bank note': ['Large Size National Bank Note', 'Small Size National Currency'],
  'national gold bank': ['Large Size National Gold Bank Note'],
  'gold certificate': ['Gold Certificate', 'Small Size Gold Certificate'],
  'treasury note': ['Treasury Note', 'Large Size Treasury / Coin Note'],
  'coin note': ['Large Size Treasury / Coin Note'],
  'demand note': ['Demand Note'],
  'confederate': ['Confederate States'],
  'fractional currency': ['Fractional Currency'],
  'postage currency': ['Fractional Currency'],
  'colonial': ['Colonial'],
  'continental currency': ['Continental Currency'],
  'state bank': ['Obsolete Note'],
  'obsolete': ['Obsolete Note'],
})
export const ALWAYS = ['Error Note']

/** The sections a printed heading can be in, or null when the map does not
 *  know it (then type narrows nothing). */
export function sectionsFor(type) {
  const t = String(type ?? '').trim().toLowerCase()
  if (!t) return null
  const hit = Object.keys(HEADING_SECTIONS).filter((k) => t.includes(k))
  if (!hit.length) return null
  return new Set([...hit.flatMap((k) => HEADING_SECTIONS[k]), ...ALWAYS])
}

const parts = (desc) => String(desc || '').split(' · ').map((s) => s.trim()).filter(Boolean)
/** Exported so the raw scorer reads a year by THIS rule, not a copy of it.
 *  ⚠ Bounded by DIGITS, not word boundaries: the v3 prompt's own example
 *  series is "1934A", and `\b` finds no year in it — found 2026-09-26 by
 *  the raw scorer, when a perfect reading lost the year axis on every
 *  lettered series (61 candidates where the year gives 22). Control A
 *  feeds bare years and could not see it. */
export const yearOfText = (s) => (String(s ?? '').match(/(?<!\d)(?:1[6-9]\d\d|20\d\d)(?!\d)/) || [null])[0]
const normPlace = (s) => String(s ?? '').trim().toLowerCase().replace(/^(the )?(state|colony|province|commonwealth) of /, '').replace(/\s+/g, ' ')

/** A read district → one of the twelve cities, or null. ⚠ HIS RULING
 *  2026-09-27: *"A district letter shows as its city (C = Philadelphia)."*
 *  The bank letters run A–L in district order, so the letter is the
 *  city's position in FRB_DISTRICTS. Anything else — "Washington, D.C."
 *  (239's front), a letter past L — is not a district and narrows nothing,
 *  as before. */
export function districtOf(v) {
  const s = String(v ?? '').trim()
  if (/^[A-La-l]$/.test(s)) return FRB_DISTRICTS['ABCDEFGHIJKL'.indexOf(s.toUpperCase())]
  return FRB_DISTRICTS.find((c) => c.toLowerCase() === s.toLowerCase()) || null
}

const NGBN = 'Large Size National Gold Bank Note'
const SMALL_NATIONAL = 'Small Size National Currency'
const SMALL_FRBN = 'Small Size Federal Reserve Bank Note'
const LARGE_NATIONAL = 'Large Size National Bank Note'
/* the sections whose district is a Federal Reserve Bank: the Colonial and
 * Obsolete "New York" rows and the Demand Notes' payable cities share a
 * city's NAME and are not districts (round 2 of his review, measured) */
const FR_SECTIONS = new Set(['Federal Reserve', 'Small Size Federal Reserve Note', 'Large Size Federal Reserve Bank Note', SMALL_FRBN])
/** The Charter period a collector sets, or null. */
export const charterOf = (v) => ({ first: 'First', second: 'Second', third: 'Third' })[String(v ?? '').trim().toLowerCase().replace(/ charter period$/, '')] || null
/** The Variety (Type 1 / Type 2) a collector sets, or null. */
export const varietyOf = (v) => ({ 'type 1': 'Type 1', 'type 2': 'Type 2', 1: 'Type 1', 2: 'Type 2' })[String(v ?? '').trim().toLowerCase()] || null

/** The axes a CATALOGUE ROW carries — the `ruled` variant of
 *  `scripts/catalog-narrow.mjs` on today's catalogue, with the state
 *  axis also covering Obsolete notes (their first `desc` part is the
 *  state; Obsolete is outside scope A, so the pinned numbers do not move). */
export function rowAxes(r) {
  const p = parts(r.desc)
  const first = p[0] || ''
  /* ⚠ CONTINENTAL CURRENCY'S ISSUER — HIS RULING 2026-09-25: *"Continental
   * Currency was issued by the Continental Congress ("The United
   * Colonies"), never by a state. So a reading that names a state as issuer
   * excludes Continental rows."* So the rows carry that issuer, and the
   * state axis can decide. Without it the $3 Continental rows carried NO
   * state, were kept by the rule below, and his Michigan $3 read perfectly
   * returned 14 candidates instead of "no Fr". */
  /* ⚠ AND TREASURY NOTE'S — his ruling the same hour, when Continental
   * alone left his Michigan $3 at 5 candidates (the Michigan row plus four
   * $3 Treasury Notes): the Treasury Notes of 1812–1861 were a FEDERAL
   * issue, never a state's, so they carry the United States as issuer.
   * ⚠ Only these two. Every other section keeps "no state = kept": a
   * National Bank Note prints its bank's city and state, and excluding
   * it on a state reading would drop a true answer. */
  const state = (r.section === 'Colonial' || r.section === 'Obsolete Note') ? first
    : r.section === 'Continental Currency' ? CONTINENTAL_ISSUER
      : r.section === 'Treasury Note' ? FEDERAL_ISSUER : ''
  /* ⚠ HIS E, 2026-09-28: *"Fr 1932-K and 1932-L keep their text ("Dallas
   * K-B", "San Francisco L-A"); the card reads the Federal Reserve city at
   * the start of that text. Exact city match only."* — and his answer 1 of
   * 2026-09-29: the rule reads ALL FOUR rows of that shape (the second
   * 1934-J and 1934-K too), each to its own city. The start-of-text read is
   * confined to the Federal Reserve sections: a Colonial or Obsolete row
   * whose text starts with a city's name is not a district. */
  const district = FRB_DISTRICTS.includes(first) ? first : (FR_SECTIONS.has(r.section) ? cityAtStart(first) : '')
  const yearPart = (p.find((x) => /^\d{4}(-[A-Z])?$/.test(x)) || '').slice(0, 4)
  const structured = r.series ? (String(r.series).match(/\d{4}/) || [''])[0] : ''
  const colonial = r.section === 'Colonial' ? yearOfText(p[1]) || '' : ''
  /* ⚠ THE CHARTER PERIOD — the catalogue's own `charterPeriod` (Large Size
   * National Bank Notes only). ~~And HIS FACT 2026-09-27 for the gold banks:
   * "National Gold Bank Notes are 1870–1875, so they show under First
   * Charter only."~~ ⚠ HIS #3, 2026-09-28: the gold banks are their own
   * type and leave the Large Size National Bank Note list, so they are no
   * longer counted as First Charter here — the Charter period row never
   * shows for their option, and his fact stands as a fact about the notes. */
  const charter = r.charterPeriod || ''
  /* ⚠ THE VARIETY — HIS FACTS AND RULING 2026-09-27: within a small
   * National's denomination *"the Fr is decided by Type 1 or Type 2"*
   * (the row's `desc`), and *"a set Type 1 / Type 2 drops the small
   * FRBNs"* — an FRBN never has one (the source's Section XXII names no
   * Type I / II), so it carries 'none' and a set variety drops it rather
   * than keeping it as unknown. Every other section is unknown ('') and
   * kept, the rule every axis follows. */
  const variety = r.section === SMALL_NATIONAL ? ((String(r.desc || '').match(/^Type ([12])$/) || [])[1] ? `Type ${String(r.desc).match(/^Type ([12])$/)[1]}` : '')
    : r.section === SMALL_FRBN ? 'none' : ''
  return {
    section: r.section,
    denom: normDenomination(r.denom),
    star: /★/.test(r.fr || ''),
    state: normPlace(state),
    district,
    year: structured || yearPart || colonial || '',
    /* ⚠ HIS RULING 1, 2026-10-02 (the safety net): a row whose `series` is a
     * WORD a collector cannot type as a year — *Original*, the 55 First
     * Charter rows — carries it here, so `canNarrow` can tell "no year" (kept
     * by every axis, never blocks) from "a year nobody can type" (blocks). */
    yearWord: r.series && !/\d{4}/.test(String(r.series)) ? String(r.series) : '',
    charter,
    variety,
    /* ── THE STOP B AMENDMENT'S AXES, 2026-10-01 — the fields checkpoint 1
     * wrote from the source's own headings (his J): the serial colour (F),
     * the series-date position (G), the Fractional issue, design and back
     * colour (I, his answers 4 and 5), and the Nationals' back type with
     * the seal/back group it answers to (#8). Each is '' where the row
     * does not carry it, and the rule every axis follows keeps that row. */
    serial: r.serial || '',
    datePosition: r.datePosition || '',
    issue: r.issue || '',
    design: r.design || '',
    back: r.back || '',
    backType: r.backType || '',
    seal: sealOf(r),
  }
}

/** HIS E: a Federal Reserve city at the START of a text part — the city
 *  itself, or the city followed by a space (*"Dallas K-B"*). Exact match on
 *  the city's name; anything else is ''. */
export const cityAtStart = (first) => FRB_DISTRICTS.find((c) => first === c || first.startsWith(c + ' ')) || ''

/** HIS #8's FIRST QUESTION, read off a Large Size National row: Third
 *  Charter's *Red Seal* rows answer "Red Seal" and its Date Back and Plain
 *  Back rows "Blue Seal"; Second Charter's *Brown Back* rows answer "Brown
 *  Back" and its Date Back and Value Back rows "Green Back". First Charter
 *  carries no back type and answers nothing (*"First Charter: no series
 *  question"*). The follow-on ("Date Back or Plain Back?" / "Date Back or
 *  Value Back?") is the row's own `backType`. */
export function sealOf(r) {
  const b = r && r.backType
  if (!b) return ''
  if (b === 'Red Seal' || b === 'Brown Back') return b
  if (r.charterPeriod === 'Third') return 'Blue Seal'
  if (r.charterPeriod === 'Second') return 'Green Back'
  return ''
}

/* ══ THE INDEX — HIS RULING (6) ON THE STEP 6 PLAN, 2026-09-26 ══════════
 * `narrow()` used to recompute `rowAxes` for every row on every call:
 * 11.9 of its 12.8 ms over 11,614 rows (measured on his Mac). The card
 * re-narrows on every edit, on a phone, and the narrower gate's control A
 * makes 11,614 calls, so the axes are computed ONCE per catalogue array
 * and the rows are bucketed by normalised denomination. Same signature,
 * same answer — the gate asserts the indexed answer equals a plain filter
 * over `rowAxes`, and that two catalogues get two indexes.
 * ⚠ KEYED BY ARRAY IDENTITY (a WeakMap): a re-parsed catalogue is a new
 * memo, and a row edited IN PLACE after indexing is NOT seen. The app
 * never mutates `CATALOG` (custom rows live in `BY_SID` only), and every
 * gate and twin builds its edited catalogue as a new array. */
const INDEX = new WeakMap()
export function indexOf(rows) {
  let ix = INDEX.get(rows)
  if (ix) return ix
  /* ⚠ HIS (8), 2026-09-29: the four rows the book says do not exist are
   * not in the index, so the card never offers them — `HIDDEN_SIDS` above
   * is the one list; `BY_SID` still resolves them for a saved note. */
  const all = rows.filter((row) => !isHidden(row)).map((row) => ({ row, a: rowAxes(row) }))
  const byDenom = new Map()
  for (const x of all) { if (!byDenom.has(x.a.denom)) byDenom.set(x.a.denom, []); byDenom.get(x.a.denom).push(x) }
  /* the axes BY ROW — his A and C read a candidate list's axes without
   * recomputing them (the card's reveal test, the safety net's "can
   * anything left narrow this") */
  const byRow = new Map(all.map((x) => [x.row, x.a]))
  /* per section: can the star, the state or the district change a list
   * inside it — the card's detail table (his point 2 and C) reads these */
  const facts = new Map()
  for (const x of all) {
    const f = facts.get(x.a.section) || { star: false, state: false, district: false }
    if (x.a.star) f.star = true
    if (x.a.state && x.a.state !== CONTINENTAL_ISSUER.toLowerCase() && x.a.state !== FEDERAL_ISSUER.toLowerCase()) f.state = true
    if (x.a.district && FR_SECTIONS.has(x.a.section)) f.district = true
    facts.set(x.a.section, f)
  }
  ix = Object.freeze({ all, byDenom, byRow, facts })
  INDEX.set(rows, ix)
  return ix
}

/** THE CARD'S OWN "HAS IT" TEST, PER ROW — his answer 6, 2026-09-29: *"a
 *  Bank only on Federal Reserve notes, and an issuer is not a state."* The
 *  same rule `facts` applies per section, asked of one row, so his A can
 *  ask it of a LIST. */
export function rowHas(rows, row, d) {
  const a = indexOf(rows).byRow.get(row)
  if (!a) return false
  if (d === 'star_serial') return a.star
  if (d === 'state') return !!a.state && a.state !== CONTINENTAL_ISSUER.toLowerCase() && a.state !== FEDERAL_ISSUER.toLowerCase()
  if (d === 'district') return !!a.district && FR_SECTIONS.has(a.section)
  return false
}

/**
 * A v3 reading → candidates. `rows` is the catalogue.
 * Returns `{ state, count, candidates, axes }`:
 *   narrowed        1..BAR candidates
 *   did-not-narrow  more than BAR — the screen must say so, never offer
 *                   them as a suggestion (6.5)
 *   no-fr           every candidate has an EMPTY Fr — the answer is "no
 *                   Fr" and the app must not invent one (his ruling)
 *   nothing         no row survives: the reading contradicts the catalogue
 * `axes` names every axis that removed a row, so the count can be
 * explained ("14 candidates on denomination and district").
 */
export function narrow(reading, rows) {
  const r = reading && typeof reading === 'object' ? reading : {}
  const ix = Array.isArray(rows) ? indexOf(rows) : null
  let set = ix ? ix.all : []
  const axes = []
  const apply = (name, keep) => { const before = set.length; set = set.filter(keep); if (set.length < before) axes.push(name) }

  const d = String(r.denomination ?? '').trim()
  if (d) {
    const k = normDenomination(d)
    /* the bucket IS the filter `x.a.denom === k`, looked up instead of scanned */
    if (k && !k.startsWith('text:')) { const before = set.length; set = ix ? (ix.byDenom.get(k) ?? []) : []; if (set.length < before) axes.push('denomination') }
  }

  /* ⚠ A TYPE PICKED OR DERIVED is a pick-list OPTION (a catalogue section,
   * `typeOption`) and narrows by that option's sections; a READ heading
   * (`type`) narrows by the generous map. Kept apart on purpose: a read
   * "Treasury Note" must keep both Treasury sections, while the OPTION
   * "Treasury Note" is the 1812–1861 section alone. */
  const secs = r.typeOption ? optionSections(r.typeOption) : sectionsFor(r.type)
  if (secs) apply('type', (x) => secs.has(x.a.section))

  const star = String(r.star_serial ?? '').trim().toLowerCase()
  if (star === 'yes') apply('star', (x) => x.a.star)
  else if (star === 'no') apply('star', (x) => !x.a.star)

  const st = normPlace(r.state)
  if (st) apply('state', (x) => !x.a.state || x.a.state === st)

  const dist = districtOf(r.district)
  if (dist) apply('district', (x) => !x.a.district || x.a.district === dist)

  const y = yearOfText(r.series)
  if (y) apply('year', (x) => !x.a.year || x.a.year === y)

  const cp = charterOf(r.charter)
  if (cp) apply('charter', (x) => !x.a.charter || x.a.charter === cp)

  const vy = varietyOf(r.variety)
  if (vy) apply('variety', (x) => x.a.variety === '' || x.a.variety === vy)

  /* the amendment's axes — an answer keeps every row that gives it or that
   * carries nothing to answer with (his answer 4: a specimen with no issue
   * *"stays under every answer"*) */
  for (const q of QUESTIONS) {
    const v = String(r[q.field] ?? '').trim()
    if (v) apply(q.field, (x) => !x.a[q.field] || x.a[q.field] === v)
  }

  const candidates = set.map((x) => x.row)
  const count = candidates.length
  const state = count === 0 ? 'nothing'
    : candidates.every((c) => !String(c.fr || '').trim()) ? 'no-fr'
      : count > BAR ? 'did-not-narrow' : 'narrowed'
  return { state, count, candidates, axes }
}

/* ══ STEP 6 — THE CARD'S RULES: HIS RULINGS OF 2026-09-27 ════════════════
 * Pure, like everything above: in the card's values and the catalogue,
 * out what the card shows and what the narrower is given. The VIEW
 * (stop B) draws it; the strings are `pending.js`'s. The rulings, in his
 * words (BUILD_NOTES.md, RESUME HERE, the four 2026-09-27 step 6 heads):
 *   *"No generic types where the catalog splits by size. The Type list
 *   has Large Size and Small Size versions only … When the AI reads a
 *   generic heading, set the size from the series year when read (small
 *   from Series 1928, 1929 for Nationals and FRBNs …) and mark it ✨; with
 *   no year, ask "large or small?"."*
 *   *"Split Treasury Note: "Treasury Note" (1812–1861) and "Treasury
 *   (Coin) Note" (1890–91)"* — *"take the issue from a read year (before
 *   1890 "Treasury Note", 1890–91 "Treasury (Coin) Note", with ✨); with no
 *   year, ask which, like the size question."*
 *   *"add "WWII Emergency Note" to the Type pick-list."*
 *   *"Large Size National Bank Notes get a "Charter period" detail"* —
 *   *"Yes, fill Charter period from a read year, with ✨."*
 *   *"a read or derived type's detail rows follow the sections the list is
 *   narrowed over, so a small FRBN read as "National Currency" keeps its
 *   bank."*
 *   *"red seals first, then blue"*; *"when the reading carries one (e.g.
 *   1928A), sort the rows with that letter to the top; never hide the
 *   others."* */

/** THE TYPE PICK-LIST: every catalogue section but Error Note, in the
 *  order of the approved render (round 3, render 12), plus his WWII
 *  addition. An option IS a section — the label is the view's, through
 *  `typeLabel`, so the pick-list and every other place a type shows carry
 *  ONE name (his ruling). No generic type is an option. */
export const TYPE_OPTIONS = Object.freeze([
  'Colonial', 'Confederate States', 'Continental Currency', 'Demand Note',
  'Large Size Federal Reserve Bank Note', SMALL_FRBN,
  'Federal Reserve', 'Small Size Federal Reserve Note',
  'Fractional Currency',
  'Gold Certificate', 'Small Size Gold Certificate',
  'Legal Tender', 'Small Size Legal Tender',
  LARGE_NATIONAL, SMALL_NATIONAL,
  NGBN,
  'Obsolete Note',
  'Silver Certificate', 'Small Size Silver Certificate',
  'Treasury Note', 'Large Size Treasury / Coin Note',
  'WWII Emergency Note',
])
/** The sections an OPTION narrows over: itself, plus the spill his
 *  rulings keep — WWII under the small Silver Certificate and the small
 *  FRN (*"Keep WWII under both types"*), the small FRBNs under the small
 *  Nationals (they print *National Currency*). ~~The gold banks under the
 *  large Nationals~~ — gone by his #3 (2026-09-28): *"National Gold
 *  Banknotes are not Large Size National Bank Notes and should be their own
 *  type"*; the option NGBN alone reaches them. Error Note rides on every
 *  option, as on every heading. */
export const OPTION_SECTIONS = Object.freeze(Object.fromEntries(TYPE_OPTIONS.map((s) => [s, Object.freeze(
  s === 'Small Size Silver Certificate' || s === 'Small Size Federal Reserve Note' ? [s, 'WWII Emergency Note']
    : s === SMALL_NATIONAL ? [s, SMALL_FRBN] : [s])])))
export function optionSections(section) {
  const own = OPTION_SECTIONS[section]
  return own ? new Set([...own, ...ALWAYS]) : null
}

/** A read heading that names a SPLIT type: two options and the year that
 *  divides them. `name` is what the question says ("Federal Reserve Note
 *  — large or small?"); `ask` says which question. */
export const SPLITS = Object.freeze([
  { name: 'Federal Reserve Bank Note', options: ['Large Size Federal Reserve Bank Note', SMALL_FRBN], from: 1929, ask: 'size' },
  { name: 'Federal Reserve Note', options: ['Federal Reserve', 'Small Size Federal Reserve Note'], from: 1928, ask: 'size' },
  { name: 'Gold Certificate', options: ['Gold Certificate', 'Small Size Gold Certificate'], from: 1928, ask: 'size' },
  { name: 'Legal Tender', options: ['Legal Tender', 'Small Size Legal Tender'], from: 1928, ask: 'size' },
  { name: 'National Bank Note', options: [LARGE_NATIONAL, SMALL_NATIONAL], from: 1929, ask: 'size' },
  { name: 'Silver Certificate', options: ['Silver Certificate', 'Small Size Silver Certificate'], from: 1928, ask: 'size' },
  { name: 'Treasury Note', options: ['Treasury Note', 'Large Size Treasury / Coin Note'], from: 1890, ask: 'issue' },
].map((s) => Object.freeze({ ...s, options: Object.freeze(s.options) })))

/** What a read heading resolves to: ONE option (the smallest whose
 *  sections hold every section the heading can be in), else a SPLIT (the
 *  smallest whose two options hold them), else `mixed` (shown and
 *  narrowed exactly as read), or null when the map does not know it. */
export function resolveHeading(text) {
  const s = sectionsFor(text)
  if (!s) return null
  const need = [...s].filter((x) => !ALWAYS.includes(x))
  const holds = (secs) => need.every((x) => secs.includes(x))
  const one = TYPE_OPTIONS.filter((o) => holds(OPTION_SECTIONS[o])).sort((a, b) => OPTION_SECTIONS[a].length - OPTION_SECTIONS[b].length)[0]
  if (one) return { kind: 'option', section: one }
  const union = (sp) => sp.options.flatMap((o) => OPTION_SECTIONS[o])
  const split = SPLITS.filter((sp) => holds(union(sp))).sort((a, b) => union(a).length - union(b).length)[0]
  return split ? { kind: 'split', split } : { kind: 'mixed' }
}

/** THE TYPE FIELD — what it is, from the card's values and whether the
 *  collector set it:
 *    typed    the collector picked an option (✎), or set "not read" → unread
 *    read     the heading names one option (✨)
 *    derived  a split heading, its option from the READ OR CORRECTED
 *             series year by his boundary (✨), following the Series while
 *             the collector has not touched Type
 *    ask      a split heading and no year: the size or the issue question
 *    mixed    a heading two splits share: shown and narrowed as read
 *    unknown  a heading the map does not know (narrows nothing)
 *    unread   nothing read
 *  ⚠ A typed Type is NEVER overwritten by a derivation. */
export function typeState(fields, typed = {}) {
  const f = fields || {}
  if (typed.type) return TYPE_OPTIONS.includes(f.type) ? { kind: 'typed', section: f.type } : { kind: 'unread' }
  const text = String(f.type ?? '').trim()
  if (!text) return { kind: 'unread' }
  const h = resolveHeading(text)
  if (!h) return { kind: 'unknown', text }
  if (h.kind === 'option') return { kind: 'read', section: h.section, text }
  if (h.kind === 'mixed') return { kind: 'mixed', text }
  const y = yearOfText(f.series)
  /* ⚠ HIS #2, 2026-09-28: *"When a year the collector typed decides the
   * size, Type shows ✎, the same as tapping Large size / Small size."* The
   * derivation is the same; `typedYear` says whose year decided it, and the
   * view marks ✎ for the collector's and ✨ for the reader's. */
  if (y) return { kind: 'derived', section: h.split.options[+y >= h.split.from ? 1 : 0], split: h.split, year: y, text, typedYear: !!typed.series }
  return { kind: 'ask', split: h.split, text }
}

/** The Charter period a series gives — the catalogue's own map, exact on
 *  every Large Size National row (measured 2026-09-27: Original and 1875
 *  First, 1882 Second, 1902 Third). Any other year gives nothing. */
export function charterFromSeries(series) {
  const s = String(series ?? '')
  if (/original/i.test(s)) return 'First'
  return ({ 1875: 'First', 1882: 'Second', 1902: 'Third' })[yearOfText(s)] || null
}

/** Does the Type field name ONE option — picked, read as one, or derived?
 *  Only then can Charter period (Large Size National) or Variety (Small
 *  Size National) show. ⚠ HIS CATCH 2026-09-27: 'read' was left out, so a
 *  heading that resolves to one option would never show them. No heading
 *  in today's map resolves to either National option (National Currency
 *  and National Bank Note are the SPLIT, and take 'derived' with a year),
 *  so the gate asserts this function directly. */
export const isSized = (t) => !!t && ['typed', 'read', 'derived'].includes(t.kind) && !!t.section

/** The optional detail rows the TYPE's table decides. Denomination, Type and
 *  Series always show (his point 2). */
export const OPTIONAL_DETAILS = Object.freeze(['charter', 'variety', 'star_serial', 'state', 'district'])
/** The three his A offers behind "Add another detail" when the type is not known. */
export const REVEALABLE = Object.freeze(['star_serial', 'state', 'district'])
const hasRead = (field, v) => field === 'star_serial' ? ['yes', 'no'].includes(String(v ?? '').trim().toLowerCase())
  : field === 'district' ? !!districtOf(v) : !!String(v ?? '').trim()

/* ══ THE STOP B AMENDMENT'S QUESTIONS — HIS RULINGS F, G, I AND #8, WITH
 * HIS ANSWERS 2, 4, 5 OF 2026-09-29 ══════════════════════════════════════
 * Each is a card row the COLLECTOR answers (the reader never reads these:
 * *"the reader never reads seal and sees only the front"*), drawn as a
 * question with its answers, over a field checkpoint 1 wrote. The answers
 * OFFERED are the values the current list carries (*"offering only the
 * issues made at that value"*), in this order; `after` names the question
 * that must be answered first (*"for Blue Seal, THEN …"*, *"For the 50¢
 * Third Issue, THEN ask …"*). The strings the card prints are
 * `pending.js`'s. ⚠ The values are the catalogue's own strings, listed so
 * the gate can assert every catalogue value is offerable. */
export const QUESTIONS = Object.freeze([
  { field: 'seal', values: ['Red Seal', 'Blue Seal', 'Brown Back', 'Green Back'], after: 'charter' },
  { field: 'backType', values: ['Date Back', 'Plain Back', 'Value Back'], after: 'seal' },
  { field: 'serial', values: ['Red', 'Blue'], after: null },
  { field: 'datePosition', values: ['above Serial Number', 'below Serial Number', 'to right of Seal'], after: null },
  { field: 'issue', values: ['First Issue', 'Second Issue', 'Third Issue', 'Fourth Issue', 'Fifth Issue'], after: null },
  { field: 'design', values: ['Spinner', 'Justice'], after: 'issue' },
  { field: 'back', values: ['Red', 'Green'], after: 'issue' },
].map((q) => Object.freeze({ ...q, values: Object.freeze(q.values) })))
export const QUESTION_FIELDS = Object.freeze(QUESTIONS.map((q) => q.field))
/** Every detail row the card can show, in the card's order (the view's
 *  `DETAIL_ORDER` is Denomination · Type · Series, then these). */
export const CARD_DETAILS = Object.freeze(['charter', 'seal', 'backType', 'variety', 'serial', 'datePosition', 'issue', 'design', 'back', 'star_serial', 'state', 'district'])

/** THE CARD — what shows and what narrows, from the card's values.
 *    fields    the card's current values: the reader's six plus the
 *              collector's `charter`, `variety` and the questions' fields
 *    typed     which of them the collector set
 *    revealed  "Add another detail" tapped (only matters when the type is
 *              not known)
 *  Returns { type, shown, hidden, charter: { value, derived }, questions,
 *  input }. `questions[field]` is `{ answers, value, asked, filled }` for
 *  each question row shown: `asked` while it waits, `filled` when his #9
 *  answered it (✨), neither when the collector did (✎).
 *  ⚠ A FIELD THE TYPE DOES NOT SHOW IS SET ASIDE — kept on the card's
 *  memory, never narrowing unseen; setting a field aside can only widen
 *  a list. */
export function cardState(fields, typed = {}, rows = [], revealed = false) {
  const f = fields || {}
  const have = Array.isArray(rows)
  let t = typeState(f, typed)
  /* ⚠ HIS #9, 2026-09-28, ON THE TYPE QUESTION: *"The card skips a
   * question when every answer leaves the same notes and fills the answer
   * in with ✨. Covers the Treasury question (a $1 is always 1890–91, Fr.
   * 347–352) and "large or small?" where only one size exists at the read
   * denomination (small Legal Tender was made only in $1, $2, $5, $100)."*
   * An answer that leaves NO note is not an answer, so when one of the two
   * options leaves the list empty the other is filled, ✨ (`only`). */
  if (t.kind === 'ask' && have) {
    const alive = t.split.options.filter((o) => narrow({ denomination: f.denomination ?? null, series: f.series ?? null, typeOption: o }, rows).count > 0)
    if (alive.length === 1) t = { kind: 'derived', section: alive[0], split: t.split, text: t.text, only: true }
  }
  const known = !['unread', 'unknown'].includes(t.kind)
  const sized = isSized(t)
  const showCharter = sized && t.section === LARGE_NATIONAL
  /* his 1: the Charter period from a read year, ✨ — unless the collector set it */
  /* ⚠ HIS RULING 3, 2026-10-02: *"A Charter period decided by a year the
   * collector typed shows ✎, like Type does under #2."* The derivation is
   * the same; `typedYear` says whose year decided it (the shape #2 gave
   * `typeState`), and the view marks ✎ for the collector's, ✨ for the
   * reader's. ~~Charter period from a typed year keeps its ✨~~ until today. */
  const charter = !showCharter ? { value: null, derived: false, typedYear: false }
    : typed.charter ? { value: charterOf(f.charter), derived: false, typedYear: false }
      : { value: charterFromSeries(f.series), derived: !!charterFromSeries(f.series), typedYear: !!typed.series }
  const input = {
    denomination: f.denomination ?? null,
    series: f.series ?? null,
    type: ['read', 'ask', 'mixed', 'unknown'].includes(t.kind) ? t.text : null,
    typeOption: t.kind === 'typed' || t.kind === 'derived' ? t.section : null,
    star_serial: null, state: null, district: null,
    charter: charter.value,
    variety: sized && t.section === SMALL_NATIONAL ? varietyOf(f.variety) : null,
  }
  let shown, hidden = []
  const questions = {}
  if (!known) {
    /* ⚠ HIS A, 2026-09-28: *"With the type unknown, "Add another detail"
     * shows Star, State or Federal Reserve Bank only if at least one note
     * in the list has it"* — by the card's own test (`rowHas`, his answer
     * 6). A value already on the card (read or typed) shows and narrows;
     * the rest are OFFERED behind the link only when the list, narrowed by
     * what is on the card, has a note that carries them. Never Charter
     * period or Variety — each belongs to one type. */
    const onCard = REVEALABLE.filter((d) => hasRead(d, f[d]))
    for (const d of REVEALABLE) input[d] = d === 'district' ? districtOf(f.district) : f[d] ?? null
    const list = have ? narrow(input, rows).candidates : []
    const offer = REVEALABLE.filter((d) => !onCard.includes(d) && list.some((row) => rowHas(rows, row, d)))
    shown = REVEALABLE.filter((d) => onCard.includes(d) || (revealed && offer.includes(d)))
    hidden = revealed ? [] : offer
  } else {
    /* HIS 4: a picked type follows its OWN section; a read, derived, asked
     * or mixed type follows the sections the list is narrowed over */
    const secs = t.kind === 'typed' ? [t.section]
      : t.kind === 'derived' ? [...OPTION_SECTIONS[t.section]]
        : [...(sectionsFor(t.text) || [])].filter((x) => !ALWAYS.includes(x))
    const facts = have ? indexOf(rows).facts : new Map()
    const any = (k) => secs.some((s) => facts.get(s)?.[k])
    const table = OPTIONAL_DETAILS.filter((d) => d === 'charter' ? showCharter
      : d === 'variety' ? sized && t.section === SMALL_NATIONAL
        : d === 'star_serial' ? any('star') : d === 'state' ? any('state') : any('district'))
    if (table.includes('star_serial')) input.star_serial = f.star_serial ?? null
    if (table.includes('state')) input.state = f.state ?? null
    if (table.includes('district')) input.district = districtOf(f.district)
    /* THE QUESTIONS, in order, each asked of the list the ones before it
     * leave. A question whose parent is unanswered is not reached; one
     * the list can answer two ways is ASKED; one it can answer only one
     * way is his #9 — skipped and FILLED ✨; one it cannot answer at all
     * is not shown. The collector's own answer stands while the list
     * still offers it; an answer the list no longer offers is set aside
     * like any field the type does not show. */
    if (sized && have) {
      for (const q of QUESTIONS) {
        if (q.after && !input[q.after]) continue
        const list = narrow(input, rows).candidates
        const ix = indexOf(rows)
        const answers = q.values.filter((v) => list.some((row) => ix.byRow.get(row)?.[q.field] === v))
        const own = typed[q.field] && answers.includes(f[q.field]) ? f[q.field] : null
        if (own) { questions[q.field] = { answers, value: own, asked: false, filled: false }; input[q.field] = own }
        else if (answers.length >= 2) { questions[q.field] = { answers, value: null, asked: true, filled: false }; input[q.field] = null }
        else if (answers.length === 1) { questions[q.field] = { answers, value: answers[0], asked: false, filled: true }; input[q.field] = answers[0] }
      }
    }
    shown = CARD_DETAILS.filter((d) => table.includes(d) || d in questions)
  }
  return { type: t, shown, hidden, charter, questions, input }
}

/** HIS C, 2026-09-28 — THE SAFETY NET'S TEST: *"whenever nothing left on the
 *  card can narrow the list, show the whole list with signatures, however
 *  long."* Something on the card can narrow the list when it is unanswered
 *  and the list's rows do not all answer it alike: the Type question (its
 *  two answers are disjoint sections), an unread Type over rows of more
 *  than one section, an unread Denomination over rows that differ in it,
 *  an unanswered Charter period, Variety, Star, State or Bank (shown, or
 *  offered behind the link) over rows that differ, and any question still
 *  asked.
 *  ~~⚠ THE BUILDER'S READING, 2026-10-01, FOR HIS REVIEW: an unread SERIES is
 *  NOT counted.~~ ⚠ HIS RULING 1, 2026-10-02, ON THAT READING: *"an unread
 *  Series counts as "something left to narrow" only when every note in the
 *  list carries a year a collector could type (a four-digit year). So the
 *  First Charter list (its Original Series rows have none) still shows in
 *  full, and a $5 Legal Tender with no year read is the over-12 line, not
 *  the whole list."* — and, when the $5 Legal Tender list was measured to
 *  carry nine rows with NO year at all (Fr 61–63b, 195e), his word on the
 *  reading that keeps both examples: *"a row with no year does not block
 *  the list; a row carrying a year nobody can type (Original Series)
 *  does."* So an unread Series counts when the dated rows carry more than
 *  one year (a typed year would cut the list; a row with no year is kept by
 *  the axis as every axis keeps it) and no row carries a `yearWord`. The
 *  Colonial 9d with Connecticut typed (17 rows, ten years) becomes the
 *  over-12 line by this — *"The 9d Connecticut change is right."*
 *  Denomination IS counted: it can always be typed and always narrows rows
 *  that differ. */
export function canNarrow(card, cands, rows) {
  const ix = indexOf(rows)
  const ax = cands.map((row) => ix.byRow.get(row)).filter(Boolean)
  const differ = (k) => new Set(ax.map((a) => a[k])).size > 1
  const t = card.type
  if (t.kind === 'ask') return true
  if (['unread', 'unknown'].includes(t.kind) && differ('section')) return true
  if (!String(card.input.denomination ?? '').trim() && differ('denom')) return true
  if (!yearOfText(card.input.series) && new Set(ax.map((a) => a.year).filter(Boolean)).size > 1 && !ax.some((a) => a.yearWord)) return true
  for (const d of [...card.shown, ...card.hidden]) {
    if (d === 'charter' && !card.charter.value && differ('charter')) return true
    if (d === 'variety' && !card.input.variety && differ('variety')) return true
    if (d === 'star_serial' && !['yes', 'no'].includes(String(card.input.star_serial ?? '').trim().toLowerCase()) && differ('star')) return true
    if (d === 'state' && !normPlace(card.input.state) && differ('state')) return true
    if (d === 'district' && !card.input.district && differ('district')) return true
    if (card.questions[d]?.asked) return true
  }
  return false
}

/** THE CARD'S LIST (stop B): `cardState` over a reading entry, and the
 *  narrower over what it says narrows. ONE call, made by the view that
 *  draws the rows AND by `pickCandidate` that guards a tap, so the list
 *  offered and the list a tap may choose from cannot drift apart.
 *  ⚠ HIS C: over the bar with nothing left to narrow it, the list is
 *  SHOWN — `res.state` is `narrowed` and `res.net` says why, so the view
 *  draws the rows and the tap's guard accepts them. */
export function narrowCard(entry, rows) {
  const e = entry || {}
  const card = cardState(e.fields, e.typed, rows, !!e.revealed)
  const res = narrow(card.input, rows)
  if (res.state === 'did-not-narrow' && Array.isArray(rows) && !canNarrow(card, res.candidates, rows)) { res.state = 'narrowed'; res.net = true }
  return { card, res }
}

/* ── THE ROWS: THE ORDER, AND WHAT A ROW SAYS ─────────────────────────── */
/** The letter of a series ("1928A", "1928-A", "Series 1928 A") or null. */
export const seriesLetter = (s) => (String(s ?? '').match(/(?<!\d)(?:1[6-9]\d\d|20\d\d)\s*-?\s*([A-Z])(?![A-Za-z])/i) || [])[1]?.toUpperCase() || null
const rowLetter = (r) => seriesLetter(r.series) || seriesLetter(parts(r.desc).find((p) => /^\d{4}-[A-Z]$/.test(p)) || '')
/** His B and 3: the rows carrying the read series letter first, then red
 *  seals, then blue, then no seal; stable, nothing hidden. The seal is the
 *  catalogue's `seal` field (a repair on his go); until it lands every
 *  row ranks alike and the catalogue's order stands. */
export function orderCandidates(cands, reading = {}) {
  const L = seriesLetter(reading.series)
  const rank = (c) => (L && rowLetter(c) === L ? 0 : 10) + ({ Red: 0, Blue: 1 }[c.seal] ?? 2)
  return cands.map((c, i) => ({ c, i })).sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i).map((x) => x.c)
}
/** The approved row strings (round 2 E, round 3's seal). ⚠ The series is
 *  `seriesText` (note-filters.js), ONE rule with the note screen's detail
 *  line — his ruling 4 of 2026-10-02 reaches both: the year alone on
 *  Confederate, Colonial and Demand Note rows. */
export const ROW_TEXT = Object.freeze({ printed: (n) => `${n} printed`, seal: (s) => `${s} Seal`, series: (s, section) => seriesText(s, section) })
/* ⚠ A SIGNATURE PAIR IS ANY "A / B" PART, UNANCHORED — round 2 found the
 * anchored rule in scripts/raw-truth.mjs (`signaturePairsFromDesc`) misses
 * 27 of 2,068 pairs: "Van Zandt" (a two-word surname) and the annotated
 * ones ("Cross / Miller (pen signed)", which is its own Fr). A row that
 * dropped them would hide the thing that decides the Fr. */
const isSigPart = (p) => /\S \/ \S/.test(p)
const isQtyPart = (p) => /^\d{1,3}(,\d{3})+\*?$/.test(p)
/** What a candidate row says: `sigs`, every signature pair on its own
 *  line with the seal after the last (*"Burke / McAdoo, Red Seal"*); and
 *  `rest`, the section (through `labelOf`, dropped when every row shares
 *  it), the series (the row's `series` where `desc` lacks it), the issue
 *  after the series (*Hawaii*, *North Africa* — a repair on his go), and
 *  the rest of `desc` with every quantity labelled *printed*. */
/** HIS #8, 2026-09-28: *"First Charter rows show the book's distinguishing
 *  detail, short, e.g. "Blue serial" / "Red serial, no charter no." / "Red
 *  serial, charter no.""* The source's Seal column is one `desc` part on
 *  111 of the 112 First Charter rows (Fr 465 *Unknown*), in these nine
 *  forms and no other (measured 2026-10-01); each maps to its short form
 *  here, by name — the seal's own description (*Red with rays*, *Red with
 *  scallops*) is dropped, because every row of a series shares it and the
 *  series is already on the row. ⚠ A form not in this table is printed as
 *  it stands, never shortened by a rule. */
export const FIRST_CHARTER_DETAIL = Object.freeze({
  'Red with rays, blue serial no.': 'Blue serial',
  'Red with rays, red serial no., without bank charter no.': 'Red serial, no charter no.',
  'Red with rays, red serial no., with bank charter no.': 'Red serial, charter no.',
  'Red with rays, red serial no.': 'Red serial',
  'Red with rays, without bank charter no.': 'No charter no.',
  'Red with rays, with bank charter no.': 'Charter no.',
  'Red with scallops': '',
  'Red with rays': '',
  'Unknown': '',
})
export function candidateText(row, { dropSection = false, labelOf = (s) => s } = {}) {
  let p = parts(row.desc)
  if (row.section === LARGE_NATIONAL && row.charterPeriod === 'First') p = p.flatMap((x) => (x in FIRST_CHARTER_DETAIL ? (FIRST_CHARTER_DETAIL[x] ? [FIRST_CHARTER_DETAIL[x]] : []) : [x]))
  const sigs = []
  if (row.signatures) sigs.push(String(row.signatures))
  for (const x of p) if (isSigPart(x) && !sigs.includes(x)) sigs.push(x)
  if (row.seal) { if (sigs.length) sigs[sigs.length - 1] += `, ${ROW_TEXT.seal(row.seal)}`; else sigs.push(ROW_TEXT.seal(row.seal)) }
  const rest = p.filter((x) => !isSigPart(x)).map((x) => (isQtyPart(x) ? ROW_TEXT.printed(x) : x))
  if (row.series && !String(row.desc || '').startsWith(row.series)) rest.unshift(ROW_TEXT.series(row.series, row.section))
  if (row.issue) {
    const at = rest.findIndex((x) => /^(Series |Original Series)|^\d{4}(-[A-Z])?$/.test(x))
    rest.splice(at >= 0 ? at + 1 : 0, 0, String(row.issue))
  }
  if (!dropSection) rest.unshift(labelOf(row.section))
  return { sigs, rest: rest.join(' · ') }
}
/** Does every row share one section (so the row can drop it)? */
export const sharesSection = (cands) => cands.length > 0 && new Set(cands.map((c) => c.section)).size === 1
